// useDebounce.js
import { useState, useEffect, useRef, useCallback } from 'react';

/**
 * Custom hook that debounces a value
 * @param {any} value - The value to debounce
 * @param {number} delay - The delay in milliseconds
 * @param {number} minLength - Minimum length for the value to trigger debounce (default: 0)
 * @returns {any} - The debounced value
 */
const useDebounce = (value, delay, minLength = 0) => {
  const [debouncedValue, setDebouncedValue] = useState(value);
  const timeoutRef = useRef(null);

  useEffect(() => {
    // Clear existing timeout
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
    }

    // If value is too short, don't debounce
    if (value && typeof value === 'string' && value.length < minLength) {
      setDebouncedValue('');
      return;
    }

    // Set new timeout
    timeoutRef.current = setTimeout(() => {
      setDebouncedValue(value);
    }, delay);

    // Cleanup function
    return () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
    };
  }, [value, delay, minLength]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
    };
  }, []);

  return debouncedValue;
};

/**
 * Custom hook for search with debouncing and minimum length requirement
 * @param {string} searchTerm - The search term
 * @param {number} delay - The delay in milliseconds (default: 500)
 * @param {number} minLength - Minimum length for search (default: 3)
 * @returns {object} - Object containing debouncedValue, isSearching, and canSearch
 */
const useSearchDebounce = (searchTerm, delay = 500, minLength = 3) => {
  const [isSearching, setIsSearching] = useState(false);
  const [debouncedValue, setDebouncedValue] = useState('');
  const timeoutRef = useRef(null);
  const lastSearchRef = useRef('');

  useEffect(() => {
    // Clear existing timeout
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
    }

    // If search term is too short, clear debounced value immediately
    if (!searchTerm || searchTerm.length < minLength) {
      setDebouncedValue('');
      setIsSearching(false);
      lastSearchRef.current = '';
      return;
    }

    // Set searching state
    setIsSearching(true);

    // Set new timeout for debounced value
    timeoutRef.current = setTimeout(() => {
      // Only update if this is still the current search term
      if (searchTerm === lastSearchRef.current || lastSearchRef.current === '') {
        setDebouncedValue(searchTerm);
        lastSearchRef.current = searchTerm;
        setIsSearching(false);
      }
    }, delay);

    // Update last search ref immediately
    lastSearchRef.current = searchTerm;

    // Cleanup function
    return () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
    };
  }, [searchTerm, delay, minLength]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
    };
  }, []);

  const canSearch = searchTerm && searchTerm.length >= minLength;
  const isDebounced = debouncedValue === searchTerm && canSearch;

  return {
    debouncedValue,
    isSearching,
    canSearch,
    isDebounced
  };
};

/**
 * Custom hook for search API calls with proper debouncing and cancellation
 * @param {function} searchFunction - The search function to call
 * @param {string} searchTerm - The search term
 * @param {number} delay - The delay in milliseconds (default: 500)
 * @param {number} minLength - Minimum length for search (default: 3)
 * @returns {object} - Object containing debouncedValue, isSearching, and canSearch
 */
const useSearchAPI = (searchFunction, searchTerm, delay = 500, minLength = 3) => {
  const [isSearching, setIsSearching] = useState(false);
  const timeoutRef = useRef(null);
  const abortControllerRef = useRef(null);
  const lastSearchTermRef = useRef('');

  useEffect(() => {
    // Clear existing timeout
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
    }

    // Cancel previous request if it exists
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
    }

    // If search term is too short, don't search
    if (!searchTerm || searchTerm.length < minLength) {
      setIsSearching(false);
      lastSearchTermRef.current = '';
      return;
    }

    // Skip if this is the same search term as the last one
    if (searchTerm === lastSearchTermRef.current) {
      return;
    }

    // Set searching state
    setIsSearching(true);
    lastSearchTermRef.current = searchTerm;

    // Set new timeout for search
    timeoutRef.current = setTimeout(async () => {
      try {
        // Double-check that this is still the current search term
        if (searchTerm !== lastSearchTermRef.current) {
          return;
        }

        // Create new abort controller for this request
        abortControllerRef.current = new AbortController();
        
        // Call search function
        await searchFunction(searchTerm, abortControllerRef.current.signal);
      } catch (error) {
        // Don't log aborted requests as errors
        if (error.name !== 'AbortError') {
          console.error('Search error:', error);
        }
      } finally {
        // Only set searching to false if this request wasn't aborted and is still current
        if (abortControllerRef.current && !abortControllerRef.current.signal.aborted && searchTerm === lastSearchTermRef.current) {
          setIsSearching(false);
        }
      }
    }, delay);

    // Cleanup function
    return () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
      }
    };
  }, [searchTerm, delay, minLength]); // Removed searchFunction from dependencies

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
      }
    };
  }, []);

  const canSearch = searchTerm && searchTerm.length >= minLength;

  return {
    isSearching,
    canSearch
  };
};

/**
 * Stable debounced search hook that prevents multiple API calls and focus loss
 * @param {string} searchTerm - The search term
 * @param {number} delay - The delay in milliseconds (default: 500)
 * @param {number} minLength - Minimum length for search (default: 3)
 * @returns {object} - Object containing debouncedValue, isSearching, and canSearch
 */
const useStableSearchDebounce = (searchTerm, delay = 500, minLength = 3) => {
  const [debouncedValue, setDebouncedValue] = useState('');
  const [isSearching, setIsSearching] = useState(false);
  const timeoutRef = useRef(null);
  const lastProcessedRef = useRef('');

  useEffect(() => {
    // Clear existing timeout
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
    }

    // If search term is too short, clear immediately but don't trigger search
    if (!searchTerm || searchTerm.length < minLength) {
      if (debouncedValue !== '') {
        setDebouncedValue('');
      }
      if (isSearching) {
        setIsSearching(false);
      }
      lastProcessedRef.current = '';
      return;
    }

    // Don't set searching state if it's already searching for the same term
    if (lastProcessedRef.current !== searchTerm) {
      setIsSearching(true);
    }

    // Set new timeout
    timeoutRef.current = setTimeout(() => {
      // Only update if this is still the current search term
      if (searchTerm === lastProcessedRef.current || lastProcessedRef.current === '') {
        setDebouncedValue(searchTerm);
        lastProcessedRef.current = searchTerm;
        setIsSearching(false);
      }
    }, delay);

    // Update last processed ref immediately
    lastProcessedRef.current = searchTerm;

    // Cleanup function
    return () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
    };
  }, [searchTerm, delay, minLength]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
    };
  }, []);

  const canSearch = searchTerm && searchTerm.length >= minLength;

  return {
    debouncedValue,
    isSearching,
    canSearch
  };
};

/**
 * Ultra-stable debounced search hook - prevents all flickering and multiple API calls
 * @param {string} searchTerm - The search term
 * @param {number} delay - The delay in milliseconds (default: 1200)
 * @param {number} minLength - Minimum length for search (default: 3)
 * @returns {object} - Object containing debouncedValue, isSearching, and canSearch
 */
const useStableDebouncedSearch = (searchTerm, delay = 1200, minLength = 3) => {
  const [debouncedValue, setDebouncedValue] = useState('');
  const [isSearching, setIsSearching] = useState(false);
  const timeoutRef = useRef(null);
  const lastProcessedRef = useRef('');
  const lastDebouncedRef = useRef('');
  const isProcessingRef = useRef(false);

  useEffect(() => {
    // Prevent multiple simultaneous processing
    if (isProcessingRef.current) {
      return;
    }

    // Clear existing timeout
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
      timeoutRef.current = null;
    }

    // If search term is too short, clear immediately
    if (!searchTerm || searchTerm.length < minLength) {
      if (debouncedValue !== '') {
        setDebouncedValue('');
        lastDebouncedRef.current = '';
      }
      if (isSearching) {
        setIsSearching(false);
      }
      lastProcessedRef.current = '';
      isProcessingRef.current = false;
      return;
    }

    // Skip if this is the same search term as what we already processed
    if (searchTerm === lastProcessedRef.current) {
      return;
    }

    // Skip if this is the same as the last debounced value
    if (searchTerm === lastDebouncedRef.current) {
      return;
    }

    // Mark as processing
    isProcessingRef.current = true;
    lastProcessedRef.current = searchTerm;

    // Set searching state
    setIsSearching(true);

    // Set new timeout
    timeoutRef.current = setTimeout(() => {
      // Only update if this is still the current search term and we're still processing
      if (searchTerm === lastProcessedRef.current && isProcessingRef.current) {
        setDebouncedValue(searchTerm);
        lastDebouncedRef.current = searchTerm;
        setIsSearching(false);
        isProcessingRef.current = false;
      }
    }, delay);

    // Cleanup function
    return () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
        timeoutRef.current = null;
      }
    };
  }, [searchTerm, delay, minLength]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
      isProcessingRef.current = false;
    };
  }, []);

  const canSearch = debouncedValue && debouncedValue.length >= minLength;

  return {
    debouncedValue,
    isSearching,
    canSearch
  };
};

export { useDebounce, useSearchDebounce, useSearchAPI, useStableSearchDebounce, useStableDebouncedSearch };
